// src/components/TriggerFlowModal/index.js

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  IconButton,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getStandardButtonStyles } from "../../styles/standardButtons";

import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    minWidth: 420,
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    border: theme.palette.type === "dark"
      ? `1px solid ${theme.palette.primary.main}40`
      : `1px solid ${theme.palette.primary.main}30`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.55)",
    overflow: "hidden",
  },
  dialogTitle: {
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogTitleText: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  closeButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
    },
  },
  dialogActions: {
    padding: "14px 24px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  formControl: {
    minWidth: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  customSelect: {
    borderRadius: "8px !important",
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.primary.main} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.primary.main} !important`,
    },
    "& .MuiSelect-select": {
      padding: "10.5px 14px",
      fontSize: "0.88rem",
    }
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 180,
  },
  flowDescription: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.04)",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  noFlowsMessage: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(3),
  },
  filterInfoBox: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  }
}));

const TriggerFlowModal = ({
  open,
  onClose,
  ticketId,
  ticketStatus,
  ticket,
  onFlowTriggered,
  onFlowProcessing
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [allFlows, setAllFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showAllCompanyFlows, setShowAllCompanyFlows] = useState(false);

  // Extraer marca y embudo del ticket
  const ticketBrandId = ticket?.whatsapp?.brandId || ticket?.brandId;
  const ticketBrandName = ticket?.whatsapp?.brand?.name || ticket?.brand?.name;
  const ticketKanbanId = ticket?.kanbanBoardId;

  // Carregar fluxos disponíveis
  useEffect(() => {
    const fetchFlows = async () => {
      if (!open) return;

      setLoadingFlows(true);
      try {
        const response = await api.get("/flowbuilder");
        const flowsData = response.data.flows || [];
        const activeFlows = flowsData.filter(flow => flow.active === true);
        setAllFlows(activeFlows);
      } catch (error) {
        console.error("Erro ao carregar fluxos:", error);
        toastError(i18n.t("triggerFlowModal.toasts.errorLoading"));
      } finally {
        setLoadingFlows(false);
      }
    };

    fetchFlows();
  }, [open]);

  // Reset ao abrir/fechar modal
  useEffect(() => {
    if (open) {
      setSelectedFlow("");
      setConfirmOpen(false);
      setShowAllCompanyFlows(false);
    }
  }, [open]);

  // Informar componente pai sobre o estado de processamento
  useEffect(() => {
    if (onFlowProcessing) {
      onFlowProcessing(loading);
    }
  }, [loading, onFlowProcessing]);

  // Filtragem inteligente de fluxos por Marca e Embudo do ticket
  const filteredFlows = React.useMemo(() => {
    if (showAllCompanyFlows) {
      return allFlows;
    }

    return allFlows.filter((flow) => {
      // Se o ticket tem marca vinculada, o fluxo deve ser dessa marca OU ser global (sem marca)
      if (ticketBrandId) {
        const matchBrand = flow.brandId === ticketBrandId || !flow.brandId;
        if (!matchBrand) return false;
      }

      // Se o ticket tem embudo vinculado e o fluxo tem embudo específico
      if (ticketKanbanId && flow.kanbanId) {
        if (flow.kanbanId !== ticketKanbanId) return false;
      }

      return true;
    });
  }, [allFlows, ticketBrandId, ticketKanbanId, showAllCompanyFlows]);

  const handleFlowChange = (event) => {
    setSelectedFlow(event.target.value);
  };

  const handleTriggerFlowWrapper = () => {
    if (!selectedFlow) {
      toast.warning(i18n.t("triggerFlowModal.toasts.selectFlow"));
      return;
    }

    if (ticketStatus === "open") {
      setConfirmOpen(true);
    } else {
      handleTriggerFlow();
    }
  };

  const handleTriggerFlow = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/tickets/${ticketId}/trigger-flow`, {
        flowId: selectedFlow
      });

      if (response.data.success) {
        toast.success(i18n.t("triggerFlowModal.toasts.success"));

        if (onFlowTriggered) {
          onFlowTriggered(response.data.data);
        }

        onClose();
      }
    } catch (error) {
      console.error("Erro ao disparar fluxo:", error);
      const errorMessage = error.response?.data?.error || i18n.t("triggerFlowModal.toasts.errorTrigger");
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const selectedFlowInfo = allFlows.find(flow => flow.id === selectedFlow);

  return (
    <>
      <ConfirmationModal
        title={i18n.t("triggerFlowModal.confirmation.title")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          handleTriggerFlow();
        }}
      >
        {i18n.t("triggerFlowModal.confirmation.message")}
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <div className={classes.dialogTitleText}>
            <FlashOnIcon style={{ color: theme.palette.primary.main }} />
            <span>{i18n.t("triggerFlowModal.title")}</span>
          </div>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ paddingTop: 16 }}>
          {loadingFlows ? (
            <div className={classes.loadingWrapper}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                {i18n.t("triggerFlowModal.description")}
              </Typography>

              {/* Indicador de Filtro Ativo por Marca/Embudo */}
              {(ticketBrandId || ticketKanbanId) && (
                <Box className={classes.filterInfoBox}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: "textSecondary" }}>
                    {i18n.t("triggerFlowModal.filteredBy")}:
                  </Typography>
                  {ticketBrandName && (
                    <Chip
                      size="small"
                      label={`${i18n.t("triggerFlowModal.brand")}: ${ticketBrandName}`}
                      color="primary"
                      variant="outlined"
                      style={{ height: 22, fontSize: "0.72rem" }}
                    />
                  )}
                  {ticketKanbanId && (
                    <Chip
                      size="small"
                      label={`${i18n.t("triggerFlowModal.board")}: #${ticketKanbanId}`}
                      variant="outlined"
                      style={{ height: 22, fontSize: "0.72rem" }}
                    />
                  )}
                </Box>
              )}

              {filteredFlows.length === 0 ? (
                <div className={classes.noFlowsMessage}>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("triggerFlowModal.noFlows")}
                  </Typography>
                </div>
              ) : (
                <FormControl className={classes.formControl} variant="outlined" size="small">
                  <InputLabel id="flow-select-label">
                    {i18n.t("triggerFlowModal.selectFlow")}
                  </InputLabel>
                  <Select
                    labelId="flow-select-label"
                    value={selectedFlow}
                    onChange={handleFlowChange}
                    label={i18n.t("triggerFlowModal.selectFlow")}
                    disabled={loading}
                    className={classes.customSelect}
                  >
                    {filteredFlows.map((flow) => (
                      <MenuItem key={flow.id} value={flow.id}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <span>{flow.name}</span>
                          <span style={{ fontSize: "0.75rem", opacity: 0.7, marginLeft: 8 }}>
                            {flow.brand?.name ? `[${flow.brand.name}]` : `[${i18n.t("triggerFlowModal.globalFlow")}]`}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Switch opcional para ver todos os fluxos da empresa se necessário */}
              {(ticketBrandId || ticketKanbanId) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAllCompanyFlows}
                      onChange={(e) => setShowAllCompanyFlows(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("triggerFlowModal.showAll")}
                    </Typography>
                  }
                  style={{ marginTop: 2, marginBottom: 8 }}
                />
              )}

              {selectedFlowInfo && (
                <Box className={classes.flowDescription}>
                  <Typography variant="caption" display="block">
                    <strong>{i18n.t("triggerFlowModal.selectedFlow")}:</strong> {selectedFlowInfo.name}
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    <strong>{i18n.t("triggerFlowModal.flowId")}:</strong> {selectedFlowInfo.id}
                  </Typography>
                </Box>
              )}

              {loading && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mt={2}
                  p={2}
                  bgcolor="action.hover"
                  borderRadius={1}
                >
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("triggerFlowModal.processing")}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            className={classes.dangerButton}
            startIcon={<CloseIcon />}
            disabled={loading}
            style={{ borderRadius: 8, textTransform: "capitalize" }}
          >
            {i18n.t("triggerFlowModal.cancel")}
          </Button>
          <Button
            onClick={handleTriggerFlowWrapper}
            className={classes.applyButton}
            startIcon={<FlashOnIcon />}
            disabled={loading || !selectedFlow || loadingFlows || filteredFlows.length === 0}
            style={{ borderRadius: 8, textTransform: "capitalize" }}
          >
            {loading
              ? i18n.t("triggerFlowModal.triggering")
              : i18n.t("triggerFlowModal.trigger")
            }
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TriggerFlowModal;
